/**
 * ChecklistItemRow - Fila individual de item
 *
 * Features para ADHD/TEA:
 * - Checkbox grande y tactil
 * - Items criticos con borde destacado
 * - Animacion de completado
 */

import { motion } from 'framer-motion';
import { AlertCircle, Trash2 } from 'lucide-react';
import { useChecklistStore } from '@/store';
import type { ChecklistItem } from '@/core/types';
import { cn } from '@/core/utils';
import { Checkbox } from '@/ui/primitives';

interface ChecklistItemRowProps {
  item: ChecklistItem;
  checklistId: string;
}

export function ChecklistItemRow({ item, checklistId }: ChecklistItemRowProps) {
  const toggleItem = useChecklistStore((state) => state.toggleItem);
  const deleteItem = useChecklistStore((state) => state.deleteItem);

  const isCritical = item.priority === 'critical';

  const handleDelete = () => {
    if (window.confirm(`¿Eliminar "${item.label}"?`)) {
      deleteItem(checklistId, item.id);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={cn(
        'group flex items-center gap-3 p-3 rounded-lg transition-colors',
        'bg-slate-800/50 hover:bg-slate-800',
        isCritical && 'border-l-2 border-rose-500',
        item.isChecked && 'opacity-60'
      )}
    >
      <Checkbox
        checked={item.isChecked}
        onChange={() => toggleItem(checklistId, item.id)}
        layer={isCritical ? 'survival' : 'maintenance'}
      />

      <div className="flex-1 min-w-0">
        <span
          className={cn(
            'text-sm',
            item.isChecked ? 'text-slate-500 line-through' : 'text-slate-200'
          )}
        >
          {item.label}
        </span>
        {item.note && (
          <p className="text-xs text-slate-500 mt-0.5">{item.note}</p>
        )}
      </div>

      {isCritical && !item.isChecked && (
        <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
      )}

      <button
        onClick={handleDelete}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-700 rounded"
        aria-label="Eliminar item"
      >
        <Trash2 className="w-3.5 h-3.5 text-slate-500 hover:text-slate-300" />
      </button>
    </motion.div>
  );
}
